import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useAppContext } from "../utils/context/app-context";
import { TextArea, FormBeforeAfterContent } from "../components/FormComponents";
import {
	Button,
	ContentContainer,
	TitleHeader,
	NarrowContentWrapper,
	AlertMessage,
	FieldsLoading
} from "../components/UiComponents";
import { CourseHeader, CourseWrapper } from "../components/CourseComponents";
import useDocumentTitle from "../components/useDocumentTitle";
import { useUserIsReadOnly } from "../components/userFunctions";

const questions = {
	risks_and_concerns: '',
	discussions: '',
	action_plan: '',
	outcome_of_action_plan: '',
}

const ReferralFormPage = () => {
	const {
		state,
		dispatch,
		api,
	} = useAppContext();
	useDocumentTitle("Referral Competency Form");
	const isOffline = state.offline;
	const { studentId } = useParams();
	const [validationErrors, setValidationErrors] = useState(null);
	const [formData, setFormData] = useState(questions);
	const [error, setError] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const [submitted, setSubmitted] = useState(false);
	const student = api.findStudentById(studentId);
	const [loading, setLoading] = useState(true);
	let course = api.getCurrentCourse();
	let date = api.getCurrentDate();
	const extraContent = state?.forms_extra_content["referral_competency"] || {};
	const isReadyOnly = useUserIsReadOnly();

	useEffect(() => {
		fetchData();
	}, []);

	async function fetchData() {
		if (isOffline) {
			let offlineForm = api.findReferralForm(studentId);
			if (offlineForm) {
				setFormData(offlineForm);
				setLoading(false);
			}
			return
		}

		// eslint-disable-next-line no-unused-vars
		const { data, error } = await api.fetchData(`/forms/referral-competency-form/${studentId}/${course.id}`);
		if (data?.form) {
			// dispatch({
			// 	type: "SET_STUDENT_DETAILS",
			// 	payload: {
			// 		student: data.student,
			// 		course_id: course.id,
			// 		date_id: state.current_date_id,
			// 	},
			// });
			setFormData(data.form);
			setLoading(false);
		} else {
			if (data?.error) {
				setError("Student not found");
			}
			setLoading(false);
		}
	}

	function handleFieldChanged(e) {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	}

	async function handleSubmit(event) {
		event.preventDefault();
		setValidationErrors(null);
		setSubmitted(false);
		setError(null);
		setSubmitting(true);

		// All fields in formData must be either 1 or 2 except for comments
		let errors = {};

		// Check every field is not empty
		// eslint-disable-next-line no-unused-vars
		for (const [key, value] of Object.entries(questions)) {
			if (!formData[key]) {
				errors[key] = "This field is required";
			}
		}
		if (Object.keys(errors).length) {
			setValidationErrors(errors);
			return;
		}

		const dataToPost = {
			student_id: student.student_id,
			course_id: course.id,
			form_data: formData
		}
		const { data, error } = await api.fetchData(
			`/forms/referral-competency-form`,
			"POST",
			dataToPost
		);
		console.log(data)
		if (data?.success) {
			dispatch({
				type: "SET_STUDENT_HAS_REFERRAL",
				payload: {
					student_id: student.student_id,
					course_id: course.id,
					date_id: date.id,
					val: 1,
				},
			});
			setSubmitting(false);
			setSubmitted(true);
		} else {
			setError(error || data?.error || "An error occurred")
			setSubmitting(false);
		}
	}

	return (
		<div className="physical-competency-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader title="Referral Competency Form" />
					<NarrowContentWrapper>
						{submitted && (
							<div className="physical-competency-page__submitted">
								<h3 className="physical-competency-page__submitted__title">
									Thank you
								</h3>
								<div className="physical-competency-page__submitted__message">
									<p>Your Referral has been submitted.</p>
									<Link to={`/courses/${course.id}/attendance-register`} className="btn btn-primary">
										Return to Attendance Register
									</Link>
								</div>
							</div>
						)}
						{loading ? (
							<FieldsLoading />
						) : (
							<>
								{(student && !submitted) && (
									<>
										<FormBeforeAfterContent content={extraContent?.before ?? null} />
										<div className="student-info-header">
											<div className="student-info-header__name">
												{student.first_name} {student.last_name}
											</div>
											<div className="student-info-header__email">
												{student.email}
											</div>
										</div>
										<form
											action=""
											className="form physical-competency-form"
											onSubmit={handleSubmit}
											noValidate>
											<TextArea
												name="risks_and_concerns"
												label="Risks/Concerns Presented"
												value={formData.risks_and_concerns}
												error={validationErrors?.risks_and_concerns}
												onChange={handleFieldChanged}
												required
											/>
											<TextArea
												name="discussions"
												label="Discussion with Participant"
												value={formData.discussions}
												error={validationErrors?.discussions}
												onChange={handleFieldChanged}
												required
											/>
											<TextArea
												name="action_plan"
												label="Discussion with Line Manager/Training Department"
												value={formData.action_plan}
												error={validationErrors?.action_plan}
												onChange={handleFieldChanged}
												required
											/>
											<TextArea
												name="outcome_of_action_plan"
												label="Action Plan Recommendation"
												value={formData.outcome_of_action_plan}
												error={validationErrors?.outcome_of_action_plan}
												onChange={handleFieldChanged}
												required
											/>
											{error && (
												<AlertMessage type="danger" classes="mb-3" message={error} />
											)}
											{validationErrors && (
												<AlertMessage type="danger" classes="mb-3" message="Please fill in all required fields" />
											)}
											<Button
												text={submitting ? "Saving..." : "Save"}
												type="primary"
												buttonType="submit"
												disabled={isReadyOnly || isOffline}
											/>
										</form>
										<FormBeforeAfterContent content={extraContent?.after ?? null} type="after" />
									</>
								)}
							</>
						)}
						{!student && (
							<AlertMessage type="danger" message="Participant not found" />
						)}
					</NarrowContentWrapper>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

export default ReferralFormPage;
